import * as React from 'react';
import { useState, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { FormInput } from '@enginuity-apps-ui-components/molecules/form-elements/form-input/form-input';
import EditableFormSection from '../../../../../ui-components/library/apps/organisms/editable-section/editable-form-section';
import { PasswordVerifierPanel } from '@enginuity-apps-ui-components/molecules/form-elements/password-verifier-panel/password-verifier-panel';
import "./styles.scss";
import { ISubmitStatus } from '../../pages/account/manage-account';

export type IManageAccountPasswordProps = {
  onSubmit: (data: IChangePasswordFormData) => Promise<ISubmitStatus>;
  email: string;
  intialEditMode?: boolean;
};

export type IChangePasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export default function ManageAccountPassword({ onSubmit, email, intialEditMode }: IManageAccountPasswordProps) {
  const { register, handleSubmit, errors } = useForm();
  const [editMode, setEditMode] = useState<boolean>(!!intialEditMode)
  const [newPassword, setNewPassword] = useState<string>("");
  const [passwordIsValid, setPasswordIsValid] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<JSX.Element | undefined>();

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.currentTarget.value);
  }

  const saveForm = (data: IChangePasswordFormData) => {
    onSubmit(data)
      .then((submitStatus: ISubmitStatus) => {
        if (submitStatus.result) {
          setEditMode(false);
        }
        else {
          setSubmitError(submitStatus.error);
        }
      })
  }

  return (
    <EditableFormSection
      sectionTitle="05. Password"
      onSubmit={handleSubmit(saveForm)}
      submitError={submitError}
      errors={errors}
      editMode={editMode}
      onEditModeChange={(mode: boolean) => setEditMode(mode)}
      editButtonText="Change password"
      saveButtonText="Change password"
    >
      <div className="row md-spacer-32 spacer-16">
        <div className="col-lg-4 current-password-conatiner">
          <FormInput
            label={<>Current password {editMode && "*"}</>}
            id="currentPassword"
            type="password"
            defaultValue={editMode ? undefined : "********"}
            ref={register({
              required: "Please enter your current password.", maxLength: {
                value: 50, message: 'Password cannot be more than 50 characters'
              }
            })}
            errors={errors}
            showReadOnly={!editMode}
            autoComplete="current-password"
          />
        </div>
      </div>
      {editMode && (
        <div className="row md-spacer-32 spacer-16">
          <div className="col-lg-4">
            <FormInput
              label={<>Password *</>}
              id="newPassword"
              type="password"
              onChange={onChange}
              ref={register({
                required: "Please enter your new password.", maxLength: {
                  value: 50, message: 'Password cannot be more than 50 characters'
                },
                validate: () => passwordIsValid || 'Password does not meet requirements'
              })}
              errors={errors}
              autoComplete="new-password"
            />
          </div>
          <div className="col-lg-4 lg-no-spacer spacer-16 confirm-password-container">
            <FormInput
              label={<>Confirm your password {editMode ?? "*"}</>}
              id="confirmPassword"
              type="password"
              ref={register({
                required: "Please confirm your new password.", maxLength: {
                  value: 50, message: 'Password cannot be more than 50 characters'
                },
                validate: value => value === newPassword || 'Passwords must match'
              })}
              errors={errors}
              autoComplete="new-password"
            />
            <p className="required-field-text color-purple-grey">{editMode ? <>* indicates required field</> : <>&nbsp;</>}</p>
          </div>
          <div className="col-lg-4 why-collect-data-panel">
            <PasswordVerifierPanel title={"How to create a strong password"} password={newPassword} username={email || ""} isValid={(val: boolean) => setPasswordIsValid(val)}>
            </PasswordVerifierPanel>
          </div>

        </div>)}
    </EditableFormSection>
  );
}
